/**
 * French purchase-cost constants, with the date they were checked.
 *
 * Every one of these is exposed as an editable input in the UI. They are
 * defaults to start from, not truths — rates move, and the only figure that
 * matters at signing is the one on your own offer. `AS_OF` is here so a stale
 * default is visible rather than silently wrong.
 */

export const AS_OF = "August 2026";

/**
 * Average mortgage rates by term, from broker barometers (CAFPI, Meilleurtaux),
 * August 2026. Best profiles get roughly 0.2 points less than these.
 */
export const MARKET_RATES: { years: number; rate: number }[] = [
  { years: 7, rate: 0.0295 },
  { years: 10, rate: 0.0302 },
  { years: 15, rate: 0.0315 },
  { years: 20, rate: 0.0331 },
  { years: 25, rate: 0.0345 },
];

/** Linear interpolation between the published terms, flat outside them. */
export function marketRate(years: number): number {
  const pts = MARKET_RATES;
  if (years <= pts[0].years) return pts[0].rate;
  if (years >= pts[pts.length - 1].years) return pts[pts.length - 1].rate;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (years <= b.years) {
      const t = (years - a.years) / (b.years - a.years);
      return a.rate + t * (b.rate - a.rate);
    }
  }
  return pts[pts.length - 1].rate;
}

/**
 * Droits de mutation à titre onéreux on an existing home.
 *
 * All eight Île-de-France departments took the increase the 2025 finance law
 * allowed (departmental share 4.50% → 5.00%), so the region is uniformly at
 * 6.32%. First-time buyers of a main residence are excluded from the increase
 * and stay at 5.81%. The increase runs to 30 April 2028.
 */
export const DMTO_ANCIEN = 0.0632;
export const DMTO_ANCIEN_FIRST_TIME = 0.0581;
/** New-build / VEFA pays a reduced duty; VAT is already inside the price. */
export const DMTO_NEUF = 0.00715;

/**
 * Notary emoluments — the regulated sliding scale, applied tranche by tranche,
 * plus 20% VAT. Extended to 29 February 2028.
 */
export const EMOLUMENT_TIERS: { upTo: number; rate: number }[] = [
  { upTo: 6_500, rate: 0.0387 },
  { upTo: 17_000, rate: 0.01596 },
  { upTo: 60_000, rate: 0.01064 },
  { upTo: Infinity, rate: 0.00799 },
];
export const VAT = 0.2;

/** Contribution de sécurité immobilière, on the price. */
export const CSI_RATE = 0.001;
/** Débours — searches, cadastre, registration. Roughly flat for a flat. */
export const DISBURSEMENTS = 1_200;

/** Borrower insurance, annual, as a share of the covered capital. */
export const INSURANCE_BANK_GROUP = 0.0034;
export const INSURANCE_DELEGATED = 0.0012;

/** Bank arrangement fee and the cost of the loan guarantee (caution). */
export const ARRANGEMENT_FEE = 1_000;
export const GUARANTEE_RATE = 0.012;

/** Running costs, per m² per year, as rough Île-de-France middles. */
export const PROPERTY_TAX_PER_M2 = 14;
export const SERVICE_CHARGE_PER_M2 = 35;
export const HOME_INSURANCE = 220;

/** HCSF lending rules: loan payment plus insurance against net income. */
export const MAX_DEBT_RATIO = 0.35;
export const MAX_TERM_YEARS = 25;
